import type z from "zod";
import type { LifeClient } from "@/client/client";
import { PluginClient } from "@/plugins/client/class";
import type { PluginClientDefinition } from "@/plugins/client/types";
import { toMethodName } from "@/shared/method-name";
import * as op from "@/shared/operation";
import type { TelemetryClient } from "@/telemetry/clients/base";
import { createTelemetryClient } from "@/telemetry/clients/browser";
import { TransportBrowserClient } from "@/transport/client/browser";
import type { agentClientConfig } from "../client/config";
import type { AgentScope } from "../server/types";
import { type AgentClientAtoms, createAgentClientAtoms } from "./atoms";
import type { AgentClientDefinition, AgentClientPluginsDefinition } from "./types";

export class AgentClient<const AgentDef extends AgentClientDefinition> {
  readonly def: AgentDef;
  readonly id: string;
  readonly name: string;
  readonly atoms: AgentClientAtoms;
  readonly config: z.output<typeof agentClientConfig.schema>;
  readonly transport: TransportBrowserClient;

  readonly #life: LifeClient;
  readonly #telemetry: TelemetryClient;
  readonly #plugins: Record<string, PluginClient<PluginClientDefinition>> = {};

  #sessionToken?: string;
  #transportRoom?: { name: string; token: string };
  #scope?: AgentScope<AgentDef["$serverDef"]["scope"]>;

  isStarted = false;

  constructor(params: {
    id: string;
    definition: AgentDef;
    config: z.output<typeof agentClientConfig.schema>;
    life: LifeClient;
  }) {
    this.def = params.definition;
    this.id = params.id;
    this.name = params.definition.name;
    this.config = params.config; // Already parsed by the server
    this.#life = params.life;

    // Initialize telemetry
    this.#telemetry = createTelemetryClient("agent.client", {
      agentId: this.id,
      agentName: this.name,
      agentConfig: this.config,
      transportProviderName: this.config.transport.provider,
    });

    // Initialize transport
    this.transport = new TransportBrowserClient({
      config: this.config.transport,
      telemetry: this.#telemetry,
    });

    // Initialize atoms
    this.atoms = createAgentClientAtoms({ agent: this, telemetry: this.#telemetry });

    // Initialize plugins
    const [errInitialize] = this.#initializePlugins(this.def.plugins);
    if (errInitialize) throw errInitialize;
  }

  #initializePlugins(plugins: AgentClientPluginsDefinition) {
    return this.#telemetry.trace("#initializePlugins()", () => {
      try {
        // Validate plugins have unique names
        const pluginNames = plugins.map((plugin) => plugin.name);
        const duplicates = pluginNames.filter((name, index) => pluginNames.indexOf(name) !== index);
        if (duplicates.length > 0) {
          const uniqueDuplicates = [...new Set(duplicates)];
          return op.failure({
            code: "Validation",
            message: `Two or more plugins are named '${uniqueDuplicates.join("', '")}'. Plugin names must be unique. (agent: '${this.name}')`,
          });
        }

        // Validate plugin dependencies
        for (const plugin of plugins) {
          for (const dependency of plugin.dependencies) {
            // - Ensure the plugin is provided
            const depPlugin = this.def.plugins.find((p) => p.name === dependency.name);
            if (!depPlugin) {
              return op.failure({
                code: "Validation",
                message: `Plugin '${plugin.name}' depends on plugin '${dependency.name}', but '${dependency.name}' is not registered. (agent: '${this.name}')`,
              });
            }
          }
        }

        // Instantiate plugins
        const initResults = plugins.map((plugin) => {
          // Create plugin instance
          const [errPlugin, pluginInstance] = op.attempt(
            () =>
              new PluginClient(
                plugin,
                (this.def.pluginConfigs[plugin.name] ?? {}) as Record<string, unknown>,
                this,
              ),
          );
          if (errPlugin) {
            return op.failure({
              code: "Unknown",
              message: `Failed to initialize plugin '${name}'.`,
              cause: errPlugin,
            });
          }
          this.#plugins[plugin.name] = pluginInstance;

          // Assign plugin instance to agent client
          const [errAccessor, accessor] = pluginInstance.getAccessor();
          if (errAccessor) return op.failure(errAccessor);
          this[toMethodName(plugin.name) as keyof typeof this] =
            accessor as this[keyof typeof this];

          return op.success();
        });

        // Log all failures
        for (const result of initResults) {
          const [error] = result;
          if (error) this.#telemetry.log.error({ error });
        }

        return op.success();
      } catch (error) {
        return op.failure({
          code: "Unknown",
          message: "Unknown error while initializing plugins.",
          cause: error,
        });
      }
    });
  }

  /**
   * Start the agent and join the transport room
   * @returns Server response on successful start
   * @throws Error if the agent fails to start
   */
  async start(scope: AgentScope<AgentDef["$serverDef"]["scope"]>) {
    return await this.#telemetry.trace("start()", async (span) => {
      const [error] = await this.#start(scope);
      if (error) {
        span.log.error({ error });
        return op.failure(error);
      }
      return op.success();
    });
  }

  // Private method, doesn't log to telemetry
  async #start(scope: AgentScope<AgentDef["$serverDef"]["scope"]>) {
    return await this.#telemetry.trace("#start()", async () => {
      try {
        // Send a call to the server to start the agent
        const [errStart, data] = await this.#life.api.call("agent.start", { id: this.id, scope });
        if (errStart) return op.failure(errStart);
        this.#sessionToken = data.sessionToken;
        this.#transportRoom = data.transportRoom;
        this.#scope = scope;

        // Join the transport room
        const [errJoin] = await this.transport.joinRoom(
          this.#transportRoom.name,
          this.#transportRoom.token,
        );
        if (errJoin) return op.failure(errJoin);

        // Start plugins
        const results = await Promise.all(Object.values(this.#plugins).map((p) => p.start()));
        const errors = results.map((r) => r[0]).filter(Boolean);
        for (const error of errors)
          this.#telemetry.log.error({ message: "Failed to start plugin.", error });

        this.isStarted = true;

        return op.success();
      } catch (error) {
        return op.failure({
          code: "Unknown",
          message: "Unknown error while starting agent.",
          cause: error,
        });
      }
    });
  }

  /**
   * Stop the agent and leave the transport room
   * @returns Server response on successful stop
   * @throws Error if the agent fails to stop
   */
  async stop() {
    return await this.#telemetry.trace("stop()", async (span) => {
      const [error] = await this.#stop();
      if (error) {
        span.log.error({ error });
        return op.failure(error);
      }
      return op.success();
    });
  }

  // Private method, doesn't log to telemetry
  async #stop() {
    return await this.#telemetry.trace("#stop()", async () => {
      try {
        // Ensure sessionToken is set
        if (!this.#sessionToken) {
          return op.failure({ code: "Conflict", message: "Agent is not started." });
        }

        // Send a call to the server to stop the agent and leave the transport room
        const [apiResult, roomResult] = await Promise.all([
          this.#life.api.call("agent.stop", {
            id: this.id,
            sessionToken: this.#sessionToken,
          }),
          this.transport.leaveRoom(),
        ]);

        // Return error if any occurs
        if (apiResult[0]) return op.failure(apiResult[0]);
        if (roomResult[0]) return op.failure(roomResult[0]);

        this.isStarted = false;

        return op.success();
      } catch (error) {
        return op.failure({
          code: "Unknown",
          message: "Unknown error while stopping agent.",
          cause: error,
        });
      }
    });
  }

  /**
   * Restart the agent by stopping and starting it
   */
  async restart() {
    return await this.#telemetry.trace("restart()", async (span) => {
      const [error] = await this.#restart();
      if (error) {
        span.log.error({ error });
        return op.failure(error);
      }
      return op.success();
    });
  }

  // Private method, doesn't log to telemetry
  async #restart() {
    return await this.#telemetry.trace("#restart()", async () => {
      try {
        const [errStop] = await this.#stop();
        if (errStop) return op.failure(errStop);
        if (!this.#scope) {
          return op.failure({ code: "Conflict", message: "Agent is not started." });
        }
        const [errStart] = await this.#start(this.#scope);
        if (errStart) return op.failure(errStart);
        return op.success();
      } catch (error) {
        return op.failure({
          code: "Unknown",
          message: "Unknown error while restarting agent.",
          cause: error,
        });
      }
    });
  }

  async enableVoiceIn() {
    const [errEnableMicrophone] = await this.transport.enableMicrophone();
    if (errEnableMicrophone) return op.failure(errEnableMicrophone);
    return op.success();
  }

  async enableVoiceOut() {
    // Turn on audio output
    // TODO

    // Play audio from the transport
    const [errPlayAudio] = await this.transport.playAudio();
    if (errPlayAudio) return op.failure(errPlayAudio);
    return op.success();
  }

  async disableVoiceIn() {
    // TODO
    return await op.success();
  }

  async disableVoiceOut() {
    // TODO
    return await op.success();
  }

  /**
   * Get agent information from the server
   * @returns Agent information including status and metrics
   * @throws Error if unable to retrieve agent info
   */
  async info() {
    return await this.#telemetry.trace("info()", async (span) => {
      const [error, data] = await this.#info();
      if (error) {
        span.log.error({ error });
        return op.failure(error);
      }
      return op.success(data);
    });
  }

  // Private method, doesn't log to telemetry
  async #info() {
    return await this.#telemetry.trace("#info()", async () => {
      try {
        // Ensure sessionToken is set
        if (!this.#sessionToken) {
          return op.failure({ code: "Conflict", message: "Agent is not started." });
        }

        // Send a call to the server to get agent information
        const [err, data] = await this.#life.api.call("agent.info", {
          id: this.id,
          sessionToken: this.#sessionToken,
        });

        // Return error if any occurs
        if (err) return op.failure(err);

        return op.success(data);
      } catch (error) {
        return op.failure({
          code: "Unknown",
          message: "Unknown error while getting agent info.",
          cause: error,
        });
      }
    });
  }
}
